import { useEffect, useState } from "react";
import { Alert, Box, Button, Grid, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { toApiLocalTimeValue, toLocalTimeInputValue } from "../../../../lib/anomaly/timeGates";
import {
  customerService,
  getCustomerApiErrorMessage,
  type Customer,
} from "../../../../services/customerService";

type CustomerWorkHoursEditorProps = {
  customer: Customer;
  onUpdated: (customer: Customer) => void;
};

const CustomerWorkHoursEditor = ({ customer, onUpdated }: CustomerWorkHoursEditorProps) => {
  const { t } = useTranslation();
  const [startWorkTime, setStartWorkTime] = useState(toLocalTimeInputValue(customer.startWorkTime) || "09:00");
  const [endWorkTime, setEndWorkTime] = useState(toLocalTimeInputValue(customer.endWorkTime) || "18:00");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setStartWorkTime(toLocalTimeInputValue(customer.startWorkTime) || "09:00");
    setEndWorkTime(toLocalTimeInputValue(customer.endWorkTime) || "18:00");
  }, [customer.id, customer.startWorkTime, customer.endWorkTime]);

  const handleSave = async () => {
    if (!customer.id) {
      return;
    }

    const start = toApiLocalTimeValue(startWorkTime);
    const end = toApiLocalTimeValue(endWorkTime);
    if ((startWorkTime && !endWorkTime) || (!startWorkTime && endWorkTime)) {
      setSaveError(t("profiles.workHoursPairRequired"));
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await customerService.updateCustomer({
        id: customer.id,
        name: customer.name || "",
        users: customer.users,
        logo: customer.logo,
        description: customer.description,
        email: customer.email,
        phone: customer.phone,
        mobilePhone: customer.mobilePhone,
        address: customer.address,
        pointOfContact: customer.pointOfContact,
        status: customer.status,
        timeZone: customer.timeZone,
        locale: customer.locale,
        startWorkTime: start,
        endWorkTime: end,
      });

      const nextCustomer: Customer = {
        ...customer,
        startWorkTime: start,
        endWorkTime: end,
      };
      onUpdated(nextCustomer);
      const range = `${toLocalTimeInputValue(start)}–${toLocalTimeInputValue(end)}`;
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            title: t("profiles.profileUpdated"),
            content:
              start && end
                ? t("profiles.workHoursSaved", { range })
                : t("profiles.workHoursCleared"),
            dateTime: Date.now(),
          },
        }),
      );
    } catch (error) {
      const message = getCustomerApiErrorMessage(
        error,
        t("profiles.workHoursSaveFailed"),
      );
      setSaveError(message);
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "error",
            title: t("profiles.profileUpdateFailed"),
            content: message,
            dateTime: Date.now(),
          },
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {t("profiles.workHours")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
        {t("profiles.workHoursHint")}
      </Typography>
      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            label={t("profiles.startWork")}
            type="time"
            size="small"
            value={startWorkTime}
            onChange={(event) => setStartWorkTime(event.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 300 } }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            label={t("profiles.endWork")}
            type="time"
            size="small"
            value={endWorkTime}
            onChange={(event) => setEndWorkTime(event.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 300 } }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
        </Grid>
        {saveError ? (
          <Grid size={12}>
            <Alert severity="error">{saveError}</Alert>
          </Grid>
        ) : null}
        <Grid size={12}>
          <Button variant="contained" onClick={() => void handleSave()} disabled={saving || !customer.id}>
            {saving ? t("profiles.saving") : t("profiles.saveWorkHours")}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerWorkHoursEditor;
